import React, { Component, createRef } from 'react'
import ReactMarkdown from 'markdown-to-jsx'
import { Link } from 'react-router-dom'
import track from 'react-tracking'
import PropTypes from 'prop-types'

import { CoremediaRenderer } from '@ycos/coremedia-renderer'
import BEMHelper from '@ynap/bem-helper'
import { Button, AsyncButton, SimpleAsyncButton } from '@ynap/button'
import { Notification } from '@ynap/notification'
import { RadioButtonGroup, RadioButton } from '@ynap/radio-button'
import { emailInfoOverlay } from '@ynap/login-and-registration-form'

import { trackSubscribeEmail, trackUpdatePreferredCountryAndLanguage } from './analytics'
import { CountryLanguageSection } from './CountryLanguageSection'
import { filterContent } from './filterUtils'
import { ConsentPreference, ConsentOptedIn, ConsentCheckbox, ConsentItem } from './CoreMediaComponents'

const bem = new BEMHelper('MarketingPreferences')

@track()
export class MarketingPreferencesForm extends Component {
    static propTypes = {
        userPreferences: PropTypes.objectOf({
            marketingConsent: PropTypes.string,
            loyaltyConsent: PropTypes.bool,
            preferredLanguage: PropTypes.string,
            preferredCountry: PropTypes.string,
            isStaff: PropTypes.bool,
            isEip: PropTypes.bool,
            gender: PropTypes.string,
        }),
        locale: PropTypes.object,
        content: PropTypes.object,
        countries: PropTypes.object,
        isFetchingUser: PropTypes.bool,
        updateUserMarketingCountryLang: PropTypes.func.isRequired,
        isUpdatingUserMarketingCountryLang: PropTypes.bool,
        updateUserMarketingCountryLangError: PropTypes.bool,
        updateUserInfo: PropTypes.func.isRequired,
        messages: PropTypes.objectOf({
            marketing: PropTypes.objectOf({
                subscribeCta: PropTypes.func,
                manageSubscriptionsCta: PropTypes.func,
                updatePreferencesCta: PropTypes.func,
            }),
        }),
        nativeApp: PropTypes.bool,
        enableGenderSelection: PropTypes.bool,
    }

    constructor(props) {
        super(props)

        this.state = {
            marketingPreferencesValues: {},
            rewardsPreferencesValues: {},
            selectedCountry: null,
            selectedLanguage: null,
            changeGenderPreference: null,
            showEmailInfoOverlay: false,
            isMktSubscribed: false,
            isMktSubscribeLoading: false,
            hasMktSubscribeError: false,
        }

        this.subscribeWrapperRef = createRef()
        this.handleCheck = this.handleCheck.bind(this)
        this.handleSubscribeClick = this.handleSubscribeClick.bind(this)
        this.handleUpdatePreferencesClick = this.handleUpdatePreferencesClick.bind(this)
        this.handleOpenEmailInfoOverlay = this.handleOpenEmailInfoOverlay.bind(this)
        this.handleCloseEmailInfoOverlay = this.handleCloseEmailInfoOverlay.bind(this)
        this.handleRemoveError = this.handleRemoveError.bind(this)
    }

    componentDidMount() {
        const { userPreferences, enableGenderSelection, countries, locale } = this.props
        const { marketingConsent, loyaltyConsent, preferredLanguage, preferredCountry, gender } = userPreferences

        document.addEventListener('click', this.handleRemoveError)

        this.setState({
            ...(marketingConsent && {
                marketingPreferencesValues: { maMarketingConsent: JSON.parse(marketingConsent) },
            }),
            ...(loyaltyConsent && { rewardsPreferencesValues: loyaltyConsent }),
        })

        if (marketingConsent === 'true') {
            this.setState({ isMktSubscribed: true })
        }

        if (preferredLanguage && preferredCountry) {
            const isCountryHiddenFromServiceData = !countries?.[preferredCountry]

            this.setState({
                selectedLanguage: isCountryHiddenFromServiceData ? locale?.langISO : preferredLanguage,
                selectedCountry: isCountryHiddenFromServiceData ? locale?.country : preferredCountry,
            })
        }

        if (enableGenderSelection && !['Female', 'Male'].includes(gender)) {
            this.setState({
                changeGenderPreference: 'Female',
            })
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { countries, userPreferences, locale } = this.props
        const { preferredLanguage, preferredCountry, marketingConsent } = userPreferences
        const { selectedCountry, selectedLanguage } = this.state

        if (marketingConsent && marketingConsent !== prevProps.userPreferences.marketingConsent) {
            this.setState({ isMktSubscribed: JSON.parse(marketingConsent) })
        }

        if (preferredLanguage && preferredLanguage !== prevProps.userPreferences.preferredLanguage) {
            const isCountryHiddenFromServiceData = !countries?.[preferredCountry]

            this.setState({ selectedLanguage: isCountryHiddenFromServiceData ? locale?.langISO : preferredLanguage })
        }

        if (preferredCountry && preferredCountry !== prevProps.userPreferences.preferredCountry) {
            const isCountryHiddenFromServiceData = !countries?.[preferredCountry]

            this.setState({ selectedCountry: isCountryHiddenFromServiceData ? locale?.country : preferredCountry })
        }

        if (selectedCountry !== prevState.selectedCountry) {
            const countryLanguages = countries[selectedCountry]?.languages || {}
            const selectedCountryPreferredLanguage = countries?.[selectedCountry]?.preferredLanguage
            const languageKeys = Object.keys(countryLanguages).map((lang) => countryLanguages[lang].locale)

            this.setState({
                selectedLanguage:
                    (languageKeys.includes(selectedLanguage) && selectedLanguage) ||
                    countryLanguages[selectedCountryPreferredLanguage]?.locale ||
                    languageKeys[0],
            })
        }
    }

    async handleSubscribeClick() {
        const { updateUserInfo, userPreferences, tracking, fetchUser } = this.props
        const { isEip } = userPreferences
        const { changeGenderPreference } = this.state

        const userUpdatePayload = {
            ...(changeGenderPreference && { gender: changeGenderPreference }),
            contextAttribute: [
                {
                    attributeName: 'maMarketingConsent',
                    attributeValue: {
                        value: 'true',
                    },
                },
            ],
        }

        this.setState({
            hasMktSubscribeError: false,
            isMktSubscribeLoading: true,
        })

        await updateUserInfo(userUpdatePayload)
            .then(() => {
                tracking?.trackEvent(trackSubscribeEmail(isEip))

                this.setState({
                    isMktSubscribed: true,
                    isMktSubscribeLoading: false,
                })
            })
            .catch(() =>
                this.setState({
                    hasMktSubscribeError: true,
                    isMktSubscribeLoading: false,
                })
            )
            .then(() => fetchUser())
    }

    handleRemoveError(e) {
        if (!this.subscribeWrapperRef?.current?.contains(e.target)) {
            this.setState({ hasMktSubscribeError: false })
        }
    }

    handleCheck(name, value) {
        this.setState({
            rewardsPreferencesValues: {
                [name]: value,
            },
        })
    }

    handleOpenEmailInfoOverlay() {
        this.setState({ showEmailInfoOverlay: true })
    }

    handleCloseEmailInfoOverlay() {
        this.setState({ showEmailInfoOverlay: false })
    }

    async handleUpdatePreferencesClick() {
        const { updateUserMarketingCountryLang, userPreferences, tracking } = this.props
        const { marketingConsent, preferredLanguage, preferredCountry } = userPreferences
        const { selectedCountry, selectedLanguage, rewardsPreferencesValues } = this.state

        const userUpdatePayload = {
            ...(marketingConsent === 'true' &&
                selectedLanguage && {
                    preferredLanguage: selectedLanguage,
                }),
            contextAttribute: [
                ...(Object.hasOwn(rewardsPreferencesValues, 'maLoyaltyConsent')
                    ? [
                          {
                              attributeName: 'maLoyaltyConsent',
                              attributeValue: {
                                  value: rewardsPreferencesValues.maLoyaltyConsent.toString(),
                              },
                          },
                      ]
                    : []),
                ...(marketingConsent === 'true' && selectedCountry
                    ? [
                          {
                              attributeName: 'maPreferredCountry',
                              attributeValue: {
                                  value: selectedCountry,
                              },
                          },
                      ]
                    : []),
            ],
        }

        await updateUserMarketingCountryLang(userUpdatePayload)

        if (selectedCountry !== preferredCountry || selectedLanguage !== preferredLanguage) {
            let eventParameter = ''

            if (selectedCountry !== preferredCountry) {
                eventParameter = 'country'
            }

            if (selectedLanguage !== preferredLanguage) {
                eventParameter += eventParameter ? '_language' : 'language'
            }

            tracking?.trackEvent(trackUpdatePreferredCountryAndLanguage(eventParameter))
        }
    }

    countryLanguagesOptions = () => {
        const { countries, messages, userPreferences } = this.props
        const { preferredLanguage } = userPreferences
        const { selectedCountry, selectedLanguage } = this.state

        if (!Object.keys(countries).length || !selectedCountry) {
            return null
        }

        const onCountryChange = (countryValue) => {
            this.setState({ selectedCountry: countryValue })
        }
        const onLanguageChange = (languageValue) => {
            this.setState({ selectedLanguage: languageValue })
        }

        return (
            <CountryLanguageSection
                countries={countries}
                messages={messages}
                selectedCountry={selectedCountry}
                selectedLanguage={selectedLanguage || preferredLanguage}
                onCountryChange={onCountryChange}
                onLanguageChange={onLanguageChange}
            />
        )
    }

    genderMarketingOptions = () => {
        const { messages, userPreferences } = this.props
        const { changeGenderPreference } = this.state
        const { gender } = userPreferences

        return (
            <RadioButtonGroup
                className={bem('preferredMAGender')}
                component={RadioButtonGroup}
                name="preferredMAGender"
                id="preferredMAGender"
                value={changeGenderPreference || gender}
            >
                <RadioButton
                    className={bem('preferredGenderRadioButton')}
                    key={messages.registrationForm.genderWomens()}
                    value="Female"
                    label={messages.registrationForm.genderWomens()}
                    onChange={(e) =>
                        this.setState({
                            changeGenderPreference: e.target.value,
                            isDirty: true,
                        })
                    }
                />
                <RadioButton
                    className={bem('preferredGenderRadioButton')}
                    key={messages.registrationForm.genderMens()}
                    value="Male"
                    label={messages.registrationForm.genderMens()}
                    onChange={(e) =>
                        this.setState({
                            changeGenderPreference: e.target.value,
                            isDirty: true,
                        })
                    }
                />
            </RadioButtonGroup>
        )
    }

    render() {
        const {
            userPreferences,
            countries,
            content,
            messages,
            locale,
            updateUserMarketingCountryLangError,
            isUpdatingUserMarketingCountryLang,
            nativeApp,
            enableGenderSelection,
        } = this.props

        const components = [ConsentPreference, ConsentOptedIn, ConsentCheckbox, ConsentItem]

        const { marketingConsent, isStaff, isEip } = userPreferences

        const {
            marketingPreferencesValues,
            rewardsPreferencesValues,
            showEmailInfoOverlay,
            isMktSubscribed,
            isMktSubscribeLoading,
            hasMktSubscribeError,
        } = this.state

        const { marketingCommunicationsItem, rewardsProgramItem } = filterContent(content, isEip, isMktSubscribed)

        const updatePreferencesAsyncState = {
            error: !!updateUserMarketingCountryLangError,
            loading: !!isUpdatingUserMarketingCountryLang,
            primaryAction: {
                message: messages.marketing.updatePreferencesCta(),
                click: this.handleUpdatePreferencesClick,
            },
        }

        const shouldDisplayUpdatePreferencesButton =
            (!isStaff && (marketingConsent === 'true' || !!rewardsProgramItem)) ||
            (isStaff && marketingConsent === 'true')

        return (
            <div className={bem(null, nativeApp ? 'nativeApp' : null)}>
                <>
                    {/* Marketing Consent Preferences */}
                    {CoremediaRenderer({ items: [marketingCommunicationsItem] }, components, {
                        rules: [
                            ({ item }) => {
                                if (item.layoutVariant == 'consent-checkbox') {
                                    return {
                                        item,
                                        form: marketingPreferencesValues,
                                        handleCheck: this.handleCheck,
                                    }
                                }
                                if (item.layoutVariant === 'consent-item') {
                                    return {
                                        item,
                                        handleOpenEmailInfoOverlay: this.handleOpenEmailInfoOverlay,
                                    }
                                }
                            },
                        ],
                    })}

                    {/* Gender Preferences */}
                    {enableGenderSelection && !isMktSubscribed && this.genderMarketingOptions()}

                    {isMktSubscribed ? (
                        <Button className={bem('opted-in-button')} type="primary-link">
                            <Link to={`/${locale.id}/account/unsubscribenewsletter`}>
                                {messages.marketing.manageSubscriptionsCta()}
                            </Link>
                        </Button>
                    ) : (
                        <div className={bem('subscribe-button-container')} ref={this.subscribeWrapperRef}>
                            {hasMktSubscribeError && (
                                <Notification
                                    className={bem('notification', 'bottom')}
                                    arrow="bottom"
                                    type="error"
                                    message={messages.marketing.updateError()}
                                />
                            )}
                            <SimpleAsyncButton
                                type="primary"
                                loading={isMktSubscribeLoading}
                                className={bem('consent-button')}
                                copy={messages.marketing.subscribeCta()}
                                onClick={this.handleSubscribeClick}
                            />
                        </div>
                    )}
                </>

                {/* Rewards Program Preferences */}
                {!isStaff && !!rewardsProgramItem && (
                    <div className={bem('loyalty-wrapper')}>
                        <h4 className={bem('consent-item-title')}>{messages.marketing.rewardsAccountSectionTitle()}</h4>
                        {CoremediaRenderer({ items: [rewardsProgramItem] }, components, {
                            rules: [
                                ({ item }) => {
                                    if (item.layoutVariant == 'consent-checkbox') {
                                        return {
                                            item,
                                            form: rewardsPreferencesValues,
                                            handleCheck: this.handleCheck,
                                        }
                                    }
                                },
                            ],
                        })}
                    </div>
                )}

                {/* Country and Language Preferences */}
                {isMktSubscribed && Object.keys(countries).length && this.countryLanguagesOptions()}

                <div className={bem('consent-more-details', marketingConsent === 'true' && 'withLanguageOptions')}>
                    <ReactMarkdown
                        options={{
                            overrides: {
                                a: (props) => (
                                    <Button
                                        type="link"
                                        component="a"
                                        target="_blank"
                                        rel="noopener"
                                        className={bem('consent-more-details-link')}
                                        href={props.href}
                                    >
                                        {props.children}
                                    </Button>
                                ),
                            },
                        }}
                    >
                        {messages.marketing.moreDetails({ locale: locale.id })}
                    </ReactMarkdown>
                </div>

                {shouldDisplayUpdatePreferencesButton && (
                    <div className={bem('consent-button-container')}>
                        <AsyncButton
                            type="primary"
                            asyncState={updatePreferencesAsyncState}
                            className={bem('consent-button')}
                            messages={{
                                notifications: {
                                    errors: messages.marketing.updateError,
                                },
                            }}
                            notificationPosition="top"
                            arrowPosition="bottom"
                        />
                    </div>
                )}

                {showEmailInfoOverlay && emailInfoOverlay(messages, locale, this.handleCloseEmailInfoOverlay)}
            </div>
        )
    }
}
